// BUILD-042 checklist assignment list, shared by My HR (self, read own tasks), HR Administration (the employee's Onboarding / Offboarding tabs) and the guided-setup
// Onboarding step.
//
// Cycle 13 layout (CHECKLIST workstream): each assigned checklist is a block with its name, process kind, assigned date, "N of M tasks done" progress and an expandable
// list of task rows (title, due date, responsible label, status chip + control, change history, linked-document info). Distinguishes:
//   - due date (computed, static) vs. actual completion time (status_changed_at, only once the task is actually completed) vs. who last changed it
//   - "assigned" (assignedAt) vs. "last changed" per task, never conflating the two
//   - a per-task change-history trail (GET /hr/checklist-tasks/:id/history), so a reopen/correction is visible, not silently relabeled as the original completion
//
// Document link: the selector is hidden behind a "Link document" button. Once linked, the authorized document name is a link to that exact assignment in the employee's
// Documents section, with its status chip and Change / Unlink. Linking is evidence only: it never signs, approves or completes anything, and the server's completion gate
// (a linked task cannot be Completed until HR marked the document reviewed complete) stays authoritative; its message is shown next to the task.
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  updateHrChecklistTaskStatus, listHrChecklistTaskHistory, linkHrChecklistTaskToDocument,
  type HrChecklistAssignment, type HrDocumentAssignment,
} from '../../api/client';
import { formatLocalDate, formatLocalDateTime, resolveDisplayName } from './hrFormat';
import { CollapsibleHelp } from './CollapsibleHelp';
import { doneCount, documentSectionPath, holdsFocus, progressLabel, sortNewestFirst } from './checklist/checklistModel';
import { TransientNotice, useTransientNotice } from './checklist/ChecklistWorkArea';
// BUILD-049: HR (company scope) can name the person who does a task; passed only from HR Administration (`assigneeAdmin`).
import { TaskAssigneeControl, useAssigneeOverlay } from './checklist/TaskAssignee';

// Document-required task labels, same vocabulary AdminDocumentsSection already uses so a linked task and its document read consistently in one place.
const DOC_STATUS_LABEL: Record<HrDocumentAssignment['status'], string> = {
  awaiting_return: 'Awaiting return',
  awaiting_hr_review: 'Awaiting HR review',
  needs_correction: 'Needs correction',
  reviewed_complete: 'Reviewed complete',
  cancelled: 'Cancelled',
};
const DOC_STATUS_TONE: Record<HrDocumentAssignment['status'], string> = {
  awaiting_return: 'bg-gray-100 text-gray-600', awaiting_hr_review: 'bg-amber-50 text-amber-700',
  needs_correction: 'bg-red-50 text-red-700', reviewed_complete: 'bg-green-50 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

const STATUS_LABEL: Record<string, string> = {
  not_started: 'Not started', in_progress: 'In progress', completed: 'Completed', exception: 'Exception / N/A',
};
const STATUS_TONE: Record<string, string> = {
  not_started: 'bg-gray-100 text-gray-600', in_progress: 'bg-amber-50 text-amber-700',
  completed: 'bg-green-50 text-green-700', exception: 'bg-gray-100 text-gray-500',
};
const KIND_LABEL: Record<string, string> = { onboarding: 'Onboarding', offboarding: 'Offboarding' };

type History = Awaited<ReturnType<typeof listHrChecklistTaskHistory>>['history'];

export function ChecklistAssignmentsPanel({
  assignments, nameMap, onChanged, canEdit = true, showHelp = true, documentAssignments, documentsUnavailable = false, focusTaskId,
  variant = 'full', renderViewTasks, assigneeAdmin,
}: {
  /** BUILD-049: set only by HR Administration (the employee whose checklists these are). Shows who does each task and lets company-scope HR name or change that person. */
  assigneeAdmin?: { employeeId: string };
  /** A pending-work or "View tasks" link names ONE assignment or ONE task; it is scrolled into view and highlighted. Presentation only. */
  focusTaskId?: string | null;
  assignments: HrChecklistAssignment[];
  nameMap?: Record<string, string | null>;
  onChanged?: () => void;
  /** false renders the caller's own self-view wording; status is still editable by the self-scope grant either way - this only changes copy, not authorization. */
  canEdit?: boolean;
  showHelp?: boolean;
  /**
   * HR Administration's own document assignments for this SAME Employee - enables the "Link document" control on each task. Omitted (undefined) in every other caller
   * (My HR self-service, guided setup) so the control does not render there; the server independently re-enforces hr.document.manage on the link route regardless.
   */
  documentAssignments?: HrDocumentAssignment[];
  /** true: the document list could not be read for this caller (denied / failed). Linked tasks then say a document is required, without a name, and no Link control is drawn. */
  documentsUnavailable?: boolean;
  /**
   * 'full' (default): task rows are shown and editable. 'summary' (guided setup): each checklist starts collapsed and its task rows are read-only; `renderViewTasks`
   * supplies the "View tasks" link that opens the exact assignment on the employee's page.
   */
  variant?: 'full' | 'summary';
  renderViewTasks?: (a: HrChecklistAssignment) => React.ReactNode;
}) {
  const summary = variant === 'summary';
  const [savingTaskId, setSavingTaskId] = useState<string | null>(null);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [collapsedOverride, setCollapsedOverride] = useState<Record<string, boolean>>({});
  const [taskHistory, setTaskHistory] = useState<Record<string, History>>({});
  const [linkingTaskId, setLinkingTaskId] = useState<string | null>(null);
  const [pickerTaskId, setPickerTaskId] = useState<string | null>(null);
  const [linkChoice, setLinkChoice] = useState('');
  const [taskError, setTaskError] = useState<Record<string, string>>({});
  const [linkNoticeTask, setLinkNoticeTask] = useState<string | null>(null);
  const linkNotice = useTransientNotice();
  const statusInFlight = useRef(false);
  const assigneeOverlay = useAssigneeOverlay(assigneeAdmin?.employeeId, assignments);

  async function handleStatusChange(assignmentTaskId: string, status: string) {
    if (statusInFlight.current) return;
    statusInFlight.current = true;
    setSavingTaskId(assignmentTaskId);
    setTaskError((m) => { const n = { ...m }; delete n[assignmentTaskId]; return n; });
    try {
      await updateHrChecklistTaskStatus(assignmentTaskId, { status: status as any });
      setTaskHistory((h) => { const n = { ...h }; delete n[assignmentTaskId]; return n; }); // the trail just gained an entry: re-read it on next open
      onChanged?.();
      if (expandedTaskId === assignmentTaskId) loadHistory(assignmentTaskId, true);
    } catch (e: any) {
      // The document-completion gate answers 409 with a specific, already user-facing message: show it next to the task instead of letting the row silently not change.
      setTaskError((m) => ({ ...m, [assignmentTaskId]: e?.message || 'Failed to update status.' }));
    } finally {
      statusInFlight.current = false;
      setSavingTaskId(null);
    }
  }

  async function handleLinkDocument(assignmentTaskId: string, documentAssignmentId: string | null) {
    setLinkingTaskId(assignmentTaskId);
    setTaskError((m) => { const n = { ...m }; delete n[assignmentTaskId]; return n; });
    try {
      await linkHrChecklistTaskToDocument(assignmentTaskId, documentAssignmentId);
      setPickerTaskId(null);
      setLinkChoice('');
      setLinkNoticeTask(assignmentTaskId);
      linkNotice.show(documentAssignmentId ? 'Document linked.' : 'Document unlinked.');
      onChanged?.();
    } catch (e: any) {
      setTaskError((m) => ({ ...m, [assignmentTaskId]: e?.message || 'Failed to update the document link.' }));
    } finally {
      setLinkingTaskId(null);
    }
  }

  function loadHistory(assignmentTaskId: string, force = false) {
    if (!force && taskHistory[assignmentTaskId]) return;
    listHrChecklistTaskHistory(assignmentTaskId)
      .then((d) => setTaskHistory((prev) => ({ ...prev, [assignmentTaskId]: d.history })))
      .catch(() => setTaskHistory((prev) => ({ ...prev, [assignmentTaskId]: [] })));
  }

  function toggleTask(assignmentTaskId: string) {
    if (expandedTaskId === assignmentTaskId) { setExpandedTaskId(null); setPickerTaskId(null); return; }
    setExpandedTaskId(assignmentTaskId);
    setPickerTaskId(null);
    loadHistory(assignmentTaskId);
  }

  if (assignments.length === 0) {
    return <div className="text-sm text-gray-400">No checklist assigned yet.</div>;
  }

  // Several assignments of the same template for the same Employee are a legitimate outcome (a deliberate re-assignment once the earlier one is finished) - show WHEN each
  // was assigned so they read as distinguishable events, not an unexplained duplicate.
  const sorted = sortNewestFirst(assignments);

  return (
    <div className="space-y-4" data-testid="checklist-assignments-list">
      {sorted.map((a, idx) => {
        const focused = focusTaskId === a.assignmentId;
        const hasFocus = holdsFocus(a, focusTaskId);
        const open = collapsedOverride[a.assignmentId] ?? (!summary || hasFocus);
        const done = doneCount(a.tasks);
        const pct = a.tasks.length ? Math.round((done / a.tasks.length) * 100) : 0;
        const bodyId = `checklist-body-${a.assignmentId}`;
        return (
          <div
            key={a.assignmentId}
            // A link may name a whole ASSIGNMENT (guided setup "View tasks"); that exact assignment is scrolled into view and highlighted. A task id still works as before.
            className={`border rounded-md p-3 ${focused ? 'border-amber-300 bg-amber-50/40 ring-2 ring-amber-300' : 'border-gray-200'}`}
            data-testid={`checklist-assignment-${a.assignmentId}`}
            data-focused={focused ? 'true' : undefined}
            ref={focused ? (el) => { if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' }); } : undefined}
          >
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
              <div className="min-w-0 flex-1 basis-56">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 break-words" data-testid="checklist-assignment-name">{a.templateNameSnapshot}</span>
                  <span className="text-[11px] font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5" data-testid="checklist-assignment-kind">{KIND_LABEL[a.processKindSnapshot] ?? a.processKindSnapshot}</span>
                  {sorted.length > 1 && <span className="text-[11px] bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">{idx === 0 ? 'Most recent' : `Assignment ${sorted.length - idx}`}</span>}
                </div>
                <div className="text-xs text-gray-500 mt-1" data-testid="checklist-assignment-meta">
                  Assigned {formatLocalDate(a.assignedAt)}{nameMap ? ` by ${resolveDisplayName(a.assignedByEmployeeId, nameMap)}` : ''}
                  <span aria-hidden="true"> · </span>Due dates counted from {formatLocalDate(a.anchorDate)}
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs font-medium text-gray-700" data-testid="checklist-progress">{progressLabel(done, a.tasks.length)}</span>
                  <span className="h-1.5 w-24 rounded-full bg-gray-200 overflow-hidden" aria-hidden="true"><span className="block h-full bg-[#4AABDE]" style={{ width: `${pct}%` }} /></span>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {renderViewTasks?.(a)}
                <button
                  type="button" aria-expanded={open} aria-controls={bodyId} data-testid="checklist-assignment-toggle"
                  onClick={() => setCollapsedOverride((m) => ({ ...m, [a.assignmentId]: !open }))}
                  className="text-xs text-[#176A9B] hover:underline whitespace-nowrap"
                >
                  {open ? 'Hide tasks' : 'Show tasks'}<span className="sr-only"> for {a.templateNameSnapshot}</span>
                </button>
              </div>
            </div>

            {open && (
              <ul id={bodyId} className="divide-y divide-gray-100 mt-2 border-t border-gray-100">
                {a.tasks.length === 0 && <li className="py-2 text-xs text-gray-500">This checklist has no tasks.</li>}
                {a.tasks.map((t) => {
                  const expanded = expandedTaskId === t.assignmentTaskId;
                  const taskFocused = focusTaskId === t.assignmentTaskId;
                  const linked = t.linkedDocumentAssignmentId && documentAssignments ? documentAssignments.find((d) => d.assignmentId === t.linkedDocumentAssignmentId) : undefined;
                  const detailsId = `checklist-task-details-${t.assignmentTaskId}`;
                  const picking = pickerTaskId === t.assignmentTaskId;
                  const isLinking = linkingTaskId === t.assignmentTaskId;
                  const canLink = !summary && !!documentAssignments && !documentsUnavailable;
                  const choices = (documentAssignments ?? []).filter((d) => d.status !== 'cancelled' || d.assignmentId === t.linkedDocumentAssignmentId);
                  return (
                    <li
                      key={t.assignmentTaskId}
                      className={`py-2 text-sm ${taskFocused ? 'bg-amber-50 ring-2 ring-amber-300 rounded-md px-2' : ''}`}
                      data-testid={`checklist-task-${t.assignmentTaskId}`}
                      data-focused={taskFocused ? 'true' : undefined}
                      ref={taskFocused ? (el) => { if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' }); } : undefined}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
                        <button
                          type="button" aria-expanded={expanded} aria-controls={detailsId} onClick={() => toggleTask(t.assignmentTaskId)}
                          className="min-w-0 flex-1 basis-48 text-left flex items-start gap-1.5 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]"
                          data-testid="checklist-task-toggle"
                        >
                          <span aria-hidden="true" className={`mt-1 inline-block text-gray-400 text-[10px] transition-transform ${expanded ? 'rotate-90' : ''}`}>▶</span>
                          <span className="min-w-0">
                            <span className="block text-gray-900 break-words" data-testid="checklist-task-title">{t.taskText}</span>
                            <span className="block text-xs text-gray-500" data-testid="checklist-task-meta">Due {formatLocalDate(t.dueDate)} · {t.responsibleRole}</span>
                          </span>
                        </button>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_TONE[t.status]}`} data-testid="checklist-task-status">{STATUS_LABEL[t.status]}</span>
                          {!summary && (
                            <select
                              className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                              value={t.status}
                              disabled={savingTaskId === t.assignmentTaskId}
                              onChange={(e) => void handleStatusChange(t.assignmentTaskId, e.target.value)}
                              aria-label={canEdit ? `Change status for ${t.taskText}` : `Update status for ${t.taskText}`}
                              data-testid="checklist-task-status-select"
                            >
                              {Object.entries(STATUS_LABEL).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                            </select>
                          )}
                        </div>
                      </div>

                      {/* A linked document is always visible (name link + status chip), never only inside the details. */}
                      {t.linkedDocumentAssignmentId && (
                        <div className="mt-1 ml-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-testid="checklist-task-link">
                          <span className="text-gray-500">Requires document:</span>
                          {linked ? (
                            <Link to={documentSectionPath(linked.employeeId, linked.assignmentId)} className="font-medium text-[#176A9B] hover:underline" data-testid="checklist-linked-document">{linked.templateName}</Link>
                          ) : (
                            <span className="font-medium text-gray-700" data-testid="checklist-linked-document">{documentsUnavailable || !documentAssignments ? 'A linked document' : 'Document not found'}</span>
                          )}
                          {linked && <span className={`px-1.5 py-0.5 rounded-full ${DOC_STATUS_TONE[linked.status]}`} data-testid="checklist-linked-status">{DOC_STATUS_LABEL[linked.status]}</span>}
                          {canLink && (
                            <>
                              <button type="button" disabled={isLinking} onClick={() => { setExpandedTaskId(t.assignmentTaskId); loadHistory(t.assignmentTaskId); setPickerTaskId(t.assignmentTaskId); setLinkChoice(t.linkedDocumentAssignmentId ?? ''); }} className="text-[#176A9B] hover:underline disabled:opacity-50" data-testid="checklist-link-change">Change<span className="sr-only"> linked document for {t.taskText}</span></button>
                              <button type="button" disabled={isLinking} onClick={() => void handleLinkDocument(t.assignmentTaskId, null)} className="text-[#176A9B] hover:underline disabled:opacity-50" data-testid="checklist-link-unlink">Unlink<span className="sr-only"> document from {t.taskText}</span></button>
                            </>
                          )}
                        </div>
                      )}
                      {assigneeAdmin && !summary && (
                        <TaskAssigneeControl
                          assignmentId={a.assignmentId} assignmentTaskId={t.assignmentTaskId} taskText={t.taskText} taskStatus={t.status}
                          overlay={assigneeOverlay.byTask[t.assignmentTaskId]} overlayStatus={assigneeOverlay.status}
                          onChanged={() => { assigneeOverlay.reload(); onChanged?.(); }}
                        />
                      )}
                      {linkNoticeTask === t.assignmentTaskId && <div className="ml-4 mt-1"><TransientNotice text={linkNotice.notice} testId="checklist-link-notice" /></div>}
                      {taskError[t.assignmentTaskId] && (
                        <p role="alert" className="mt-1.5 ml-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="checklist-task-error">{taskError[t.assignmentTaskId]}</p>
                      )}

                      {expanded && (
                        <div id={detailsId} className="mt-2 ml-4 pl-3 border-l-2 border-gray-100 space-y-2" data-testid="checklist-task-details">
                          <div className="text-xs text-gray-500">
                            {t.status === 'completed' && t.statusChangedAt && (
                              <span className="text-green-700">Completed {formatLocalDateTime(t.statusChangedAt)} by {resolveDisplayName(t.actorEmployeeId, nameMap)}</span>
                            )}
                            {t.status !== 'completed' && t.statusChangedAt && (
                              <span>Last changed {formatLocalDateTime(t.statusChangedAt)} by {resolveDisplayName(t.actorEmployeeId, nameMap)}</span>
                            )}
                            {!t.statusChangedAt && <span>No status changes yet.</span>}
                          </div>

                          {canLink && !t.linkedDocumentAssignmentId && !picking && (
                            <div>
                              <button type="button" onClick={() => { setPickerTaskId(t.assignmentTaskId); setLinkChoice(''); }} className="text-xs text-[#176A9B] border border-gray-300 rounded px-2.5 py-1 hover:bg-gray-50" data-testid="checklist-link-open">
                                Link document<span className="sr-only"> to {t.taskText}</span>
                              </button>
                            </div>
                          )}
                          {canLink && picking && (
                            <div className="flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 rounded-md p-2" data-testid="checklist-link-picker">
                              <select
                                className="border border-gray-300 rounded px-2 py-1 text-xs bg-white max-w-full" value={linkChoice} autoFocus
                                onChange={(e) => setLinkChoice(e.target.value)} aria-label={`Document to link to ${t.taskText}`} data-testid="checklist-link-select"
                              >
                                <option value="">Choose a document…</option>
                                {choices.map((d) => <option key={d.assignmentId} value={d.assignmentId}>{d.templateName} (v{d.versionNumber}) - {DOC_STATUS_LABEL[d.status]}</option>)}
                              </select>
                              <button
                                type="button" disabled={isLinking || !linkChoice || linkChoice === t.linkedDocumentAssignmentId} onClick={() => void handleLinkDocument(t.assignmentTaskId, linkChoice)}
                                className="text-xs bg-[#4AABDE] text-white rounded px-3 py-1 disabled:opacity-50" data-testid="checklist-link-confirm"
                              >{isLinking ? 'Linking…' : 'Link'}</button>
                              <button type="button" disabled={isLinking} onClick={() => setPickerTaskId(null)} className="text-xs text-gray-600 hover:underline" data-testid="checklist-link-cancel">Cancel</button>
                              {choices.length === 0 && <span className="text-xs text-gray-500 w-full">This employee has no documents assigned yet.</span>}
                              <span className="text-[11px] text-gray-500 w-full">Linking does not sign, approve or complete anything. The task can be Completed only after HR marks the document reviewed complete.</span>
                            </div>
                          )}

                          <div className="space-y-1" data-testid="checklist-task-history">
                            <div className="text-xs font-medium text-gray-600">Change history</div>
                            {!taskHistory[t.assignmentTaskId] ? (
                              <div className="text-xs text-gray-400">Loading…</div>
                            ) : taskHistory[t.assignmentTaskId].length === 0 ? (
                              <div className="text-xs text-gray-400 italic">No prior status changes.</div>
                            ) : (
                              taskHistory[t.assignmentTaskId].map((h) => (
                                <div key={h.historyId} className="text-xs text-gray-500">
                                  Was <span className="font-medium">{STATUS_LABEL[h.status] ?? h.status}</span>
                                  {h.note ? ` ("${h.note}")` : ''} - changed by {resolveDisplayName(h.changedByEmployeeId, nameMap)} on {formatLocalDateTime(h.changedAt)}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
      {showHelp && (
        <CollapsibleHelp>
          <p>Due date is when a task is expected to be done. Completed shows the actual time it was marked done, and by whom - these can differ from when it was due.</p>
          <p>Editing a checklist template never changes a checklist that was already assigned - each assignment keeps the task wording and due dates it had when it was assigned. Open a task to see every prior status.</p>
          {documentAssignments && (
            <p>Linking a task to a document means that task cannot be marked Completed until HR reviews the uploaded scan and marks it Reviewed complete - this is enforced by the server, not just this screen.</p>
          )}
        </CollapsibleHelp>
      )}
    </div>
  );
}
